import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type { StaffProfile, StaffDashboardData } from '../types/staff';
import type { ServiceTask } from '../types/appointments';
import type { PaymentSetupResponse, StripeConnectStatusResponse, StripeConnectAccountSessionResponse } from '../types/payment';
import type {
	StaffBookingsResponse,
	StaffBookingsParams,
	StaffBookingDetail,
} from '../types/staffBookings';
import type {
	StaffTcr,
	StaffTcrParams,
	StaffTcrResponse,
	TcrSubmitPayload,
	TcrUpdatePayload,
} from '../types/staffTcr';

export const staffProfileApi = {
	getProfile: async (): Promise<StaffProfile> => {
		return apiClient.get<StaffProfile>(API_ENDPOINTS.STAFF.ME);
	},

	getDashboard: async (): Promise<StaffDashboardData> => {
		return apiClient.get<StaffDashboardData>(API_ENDPOINTS.STAFF.DASHBOARD);
	},

	updatePersonalInfo: async (data: {
		firstName: string;
		lastName: string;
		phone: string;
	}): Promise<StaffProfile> => {
		return apiClient.put<StaffProfile>(API_ENDPOINTS.STAFF.UPDATE_PERSONAL_INFO, data);
	},

	updateServices: async (serviceIds: number[]): Promise<void> => {
		return apiClient.put<void>(API_ENDPOINTS.STAFF.UPDATE_SERVICES, { serviceIds });
	},

	updateTasks: async (selectedTaskIds: number[]): Promise<void> => {
		return apiClient.put<void>(API_ENDPOINTS.STAFF.UPDATE_TASKS, { selectedTaskIds });
	},

	getBookingDetail: async (id: number): Promise<StaffBookingDetail> => {
		return apiClient.get<StaffBookingDetail>(API_ENDPOINTS.STAFF.BOOKING_DETAIL(id));
	},

	getBookings: async (params: StaffBookingsParams): Promise<StaffBookingsResponse> => {
		const queryParams = new URLSearchParams();
		queryParams.append('tab', params.tab);
		if (params.search) queryParams.append('search', params.search);
		if (params.date) queryParams.append('date', params.date);
		return apiClient.get<StaffBookingsResponse>(
			`${API_ENDPOINTS.STAFF.BOOKINGS}?${queryParams.toString()}`
		);
	},

	getTcrs: async (params: StaffTcrParams): Promise<StaffTcrResponse> => {
		const queryParams = new URLSearchParams();
		if (params.status && params.status !== 'ALL') queryParams.append('status', params.status);
		if (params.search) queryParams.append('search', params.search);
		if (params.period && params.period !== 'ALL') queryParams.append('period', params.period);
		if (params.page !== undefined) queryParams.append('page', String(params.page));
		if (params.size) queryParams.append('size', String(params.size));
		return apiClient.get<StaffTcrResponse>(
			`${API_ENDPOINTS.STAFF.TCR}?${queryParams.toString()}`
		);
	},

	getTcrDetail: async (id: number): Promise<StaffTcr> => {
		return apiClient.get<StaffTcr>(API_ENDPOINTS.STAFF.TCR_DETAIL(id));
	},

	submitTcr: async (data: TcrSubmitPayload): Promise<StaffTcr> => {
		return apiClient.post<StaffTcr>(API_ENDPOINTS.STAFF.TCR, data);
	},

	updateTcr: async (id: number, data: TcrUpdatePayload): Promise<StaffTcr> => {
		return apiClient.put<StaffTcr>(API_ENDPOINTS.STAFF.TCR_DETAIL(id), data);
	},

	getTcrPhotos: async (id: number): Promise<string[]> => {
		return apiClient.get<string[]>(API_ENDPOINTS.STAFF.TCR_PHOTOS(id));
	},

	acceptAgreement: async (type: string): Promise<void> => {
		return apiClient.patch<void>(API_ENDPOINTS.STAFF.AGREEMENT(type));
	},

	updateAddress: async (data: {
		streetAddress: string;
		city: string;
		state: string;
		zipCode: string;
	}): Promise<void> => {
		return apiClient.put<void>(API_ENDPOINTS.STAFF.UPDATE_ADDRESS, data);
	},

	updateEmergencyContact: async (data: {
		name: string;
		phone: string;
		email: string;
	}): Promise<void> => {
		return apiClient.put<void>(API_ENDPOINTS.STAFF.UPDATE_EMERGENCY_CONTACT, data);
	},

	getServiceTasks: async (): Promise<ServiceTask[]> => {
		return apiClient.get<ServiceTask[]>(API_ENDPOINTS.STAFF.SERVICE_TASKS);
	},

	// ─── Payment Setup ──────────────────────────────────────────

	setupPayment: async (): Promise<PaymentSetupResponse> => {
		return apiClient.post<PaymentSetupResponse>(API_ENDPOINTS.STAFF.PAYMENT_SETUP, {});
	},

	cancelSubscription: async (): Promise<void> => {
		await apiClient.delete(API_ENDPOINTS.STRIPE_CONNECT.SUBSCRIPTION);
	},

	resumeSubscription: async (): Promise<void> => {
		await apiClient.post(API_ENDPOINTS.STRIPE_CONNECT.SUBSCRIPTION_RESUME, {});
	},

	pauseSubscription: async (): Promise<void> => {
		await apiClient.post(API_ENDPOINTS.STRIPE_CONNECT.SUBSCRIPTION_PAUSE, {});
	},

	disconnectStripeAccount: async (): Promise<void> => {
		await apiClient.delete(API_ENDPOINTS.STRIPE_CONNECT.DISCONNECT_ACCOUNT);
	},

	// ─── Stripe Connect (Automatic Payouts) ─────────────────────

	getStripeConnectStatus: async (): Promise<StripeConnectStatusResponse> => {
		return apiClient.get<StripeConnectStatusResponse>(API_ENDPOINTS.STRIPE_CONNECT.STATUS);
	},

	createConnectAccountSession: async (): Promise<StripeConnectAccountSessionResponse> => {
		return apiClient.post<StripeConnectAccountSessionResponse>(
			API_ENDPOINTS.STRIPE_CONNECT.ACCOUNT_SESSION,
			{}
		);
	},
};

export default staffProfileApi;
